import { Components } from '@/api/generated/api';
import { BillingOfferStatus, Product } from '@/api/generated/api.enum';
import type { TTranslate } from '@/interfaces';
import { dateTodMMMyyyy, dateTodMMMyyyyCompact } from '@/utils/date.utils';
import { buildPdfFileName } from 'common/utils/file.utils';
import { formatBillingAmount } from './formatting';
import { productMetaByProduct } from './product-catalog';
import {
  BILLING_STATUS_COLOR_MUTED,
  BILLING_STATUS_COLOR_PRIMARY,
  BILLING_STATUS_COLOR_WARNING,
  type BillingInvoiceStatusPresentation,
} from './types';

export function getBillingOfferTitle(offer: Components.Schemas.BillingOfferDto, t: TTranslate): string {
  return offer.stripeQuoteNumber
    ? t('billing.offers.offerTitle', { number: offer.stripeQuoteNumber })
    : t('billing.offers.fallbackOfferTitle');
}

export function getBillingOfferDateLabel(offer: Components.Schemas.BillingOfferDto): string {
  return dateTodMMMyyyy(offer.createdAt);
}

export function getBillingOfferValidUntilLabel(offer: Pick<Components.Schemas.BillingOfferDto, 'validUntil'>): string {
  return dateTodMMMyyyyCompact(offer.validUntil);
}

export function getBillingOfferProductLabel(offer: Components.Schemas.BillingOfferDto, t: TTranslate): string {
  return t(productMetaByProduct[offer.product].titleKey);
}

export function getBillingOfferProductDescription(
  offer: Pick<Components.Schemas.BillingOfferDto, 'product'>,
  t: TTranslate,
): string {
  if (offer.product === Product.school) return t('billing.offers.reviewProductDescriptionSchool');
  if (offer.product === Product.team) return t('billing.offers.reviewProductDescriptionTeam');
  return t('billing.offers.reviewProductDescriptionFallback');
}

export function formatBillingOfferAmount(offer: Components.Schemas.BillingOfferDto): string {
  return formatBillingAmount(offer.amountSubtotal, offer.currency);
}

export function getBillingOfferPdfFileName(offer: Components.Schemas.BillingOfferDto, t: TTranslate): string {
  return buildPdfFileName(t('billing.offers.pdfFilePrefix'), offer.stripeQuoteNumber ?? offer.id);
}

export function getBillingOfferAddressLines(offer: Components.Schemas.BillingOfferDto, t: TTranslate): string[] {
  return [
    offer.address.name,
    offer.address.line1,
    offer.address.city,
    offer.address.postalCode,
    offer.address.vatId ? t('billing.offers.vatLine', { vatId: offer.address.vatId }) : '',
    offer.address.notes ?? '',
  ].filter((line): line is string => Boolean(line));
}

export function getBillingOfferStatusPresentation(
  offer: Components.Schemas.BillingOfferDto,
  t: TTranslate,
): BillingInvoiceStatusPresentation {
  if (offer.status === BillingOfferStatus.pending) {
    return {
      color: BILLING_STATUS_COLOR_WARNING,
      label: t('billing.offers.status.pending'),
      theme: 'fill',
    };
  }

  if (offer.status === BillingOfferStatus.accepted) {
    return {
      color: BILLING_STATUS_COLOR_PRIMARY,
      label: t('billing.offers.status.accepted'),
      theme: 'outline',
    };
  }

  return {
    color: BILLING_STATUS_COLOR_MUTED,
    label: t(`billing.offers.status.${offer.status}`),
    theme: 'transparent',
  };
}
